import { api } from '../apiClient'
import type {
  InstanceData,
  OntologyClassDef,
  OntologyPropertyDef,
  OntologySchema,
} from '../apiTypes'

type RegistryClassType = 'Resource' | 'Activity' | 'Product'
type RegistryDataType = 'number' | 'string' | 'datetime' | 'boolean'
type RegistryCardinality = 'one' | 'many'

export type ClassRegistryProperty = {
  name: string
  dataType: RegistryDataType
  required: boolean
}

export type ClassRegistryRelation = {
  name: string
  targetClassId: string
  cardinality: RegistryCardinality
}

export type ClassRegistryEntry = {
  id: string
  projectId: string
  name: string
  classType: RegistryClassType
  description: string
  properties: ClassRegistryProperty[]
  objectProperties: ClassRegistryRelation[]
  instanceCount?: number | null
  instanceCountStatus?: 'computed' | 'not_computed' | 'sync_pending'
  instanceCountComputedAt?: string | null
  updatedAt?: string
  [key: string]: unknown
}

const unsupportedLegacyTBoxWrite = (operation: string): never => {
  throw new Error(
    `Legacy ontologyAPI.${operation} is disabled. Use the canonical project class-registry surface.`
  )
}

const classRegistryRoot = (projectId: string): string =>
  `/api/projects/${encodeURIComponent(projectId)}/class-registry`

const registryClassTypeToTBoxType = (
  classType: RegistryClassType
): string => {
  switch (classType) {
    case 'Resource':
      return 'Resource'
    case 'Activity':
      return 'Activity'
    case 'Product':
    default:
      return 'Product'
  }
}

export const registryEntriesToSchema = (
  entries: ClassRegistryEntry[]
): OntologySchema => {
  const classes: OntologyClassDef[] = entries.map((entry) => ({
    id: entry.id,
    name: entry.name,
    label: entry.name,
    class_type: registryClassTypeToTBoxType(entry.classType),
    type: entry.classType,
    description: entry.description,
    properties: entry.properties.map((prop) => ({
      id: prop.name,
      name: prop.name,
      label: prop.name,
      property_type: 'DataProperty',
      domain: entry.id,
      source_class_id: entry.id,
      range: prop.dataType,
      data_type: prop.dataType,
      required: prop.required,
    })),
    objectProperties: entry.objectProperties,
    instanceCount: entry.instanceCount,
    instanceCountStatus: entry.instanceCountStatus,
    instanceCountComputedAt: entry.instanceCountComputedAt,
  }))
  const properties: OntologyPropertyDef[] = entries.flatMap((entry) => [
    ...entry.properties.map((prop) => ({
      id: prop.name,
      name: prop.name,
      label: prop.name,
      property_type: 'DataProperty',
      domain: entry.id,
      source_class_id: entry.id,
      range: prop.dataType,
      data_type: prop.dataType,
      required: prop.required,
    })),
    ...entry.objectProperties.map((relation) => ({
      id: relation.name,
      name: relation.name,
      label: relation.name,
      relation_id: relation.name,
      property_type: 'ObjectProperty',
      domain: entry.id,
      range: relation.targetClassId,
      source_class_id: entry.id,
      target_class_id: relation.targetClassId,
      cardinality: relation.cardinality,
    })),
  ])
  return {
    classes,
    properties,
  }
}

const assertClassRegistryEntries = (value: unknown): ClassRegistryEntry[] => {
  if (!Array.isArray(value)) {
    throw new Error('Class registry response was not an array')
  }
  return value as ClassRegistryEntry[]
}

// S4 / Gap_007 — pack origin + divergence shapes returned by
// GET /class-registry/{class_id}/pack-origin and /divergence. The UI uses
// these to render the "Pack 출처" badge + "Diverged: N props" affordance
// on the class detail panel.

export type ClassPackDivergenceState = 'canonical' | 'modified_locally' | 'forked'

export type ClassPackOriginItem = {
  originId: string
  classRegistryEntryId: string
  packSharedClassDefId: string
  packId: string
  canonicalPackVersion: string
  installedPackVersion: string
  canonicalClassKey: string
  canonicalName: string
  canonicalClassType: string
  divergenceState: ClassPackDivergenceState
  lastComparedAt?: string | null
}

export type ClassPackOriginResponse = {
  classId: string
  projectId: string
  origins: ClassPackOriginItem[]
}

export type DivergencePropertyDelta = {
  name: string
  dataType?: string | null
  required?: boolean | null
  canonicalDataType?: string | null
  localDataType?: string | null
}

export type ClassDivergenceOriginItem = {
  packSharedClassDefId: string
  packId: string
  canonicalPackVersion: string
  installedPackVersion: string
  canonicalClassKey: string
  divergenceState: ClassPackDivergenceState
  diff: {
    missingInLocal: DivergencePropertyDelta[]
    addedInLocal: DivergencePropertyDelta[]
    typeDrift: DivergencePropertyDelta[]
  }
  lastComparedAt?: string | null
}

export type ClassDivergenceResponse = {
  classId: string
  projectId: string
  origins: ClassDivergenceOriginItem[]
}

export const classRegistryAPI = {
  list: (projectId: string) =>
    api.get<ClassRegistryEntry[]>(classRegistryRoot(projectId)),
  getSchema: (projectId: string) =>
    classRegistryAPI.list(projectId).then((response) => ({
      ...response,
      data: registryEntriesToSchema(assertClassRegistryEntries(response.data)),
    })),
  // S4 Gap_007 — pack origin + divergence.
  getPackOrigin: (projectId: string, classId: string) =>
    api.get<ClassPackOriginResponse>(
      `${classRegistryRoot(projectId)}/${encodeURIComponent(classId)}/pack-origin`
    ),
  getDivergence: (
    projectId: string,
    classId: string,
    { refresh = true }: { refresh?: boolean } = {}
  ) =>
    api.get<ClassDivergenceResponse>(
      `${classRegistryRoot(projectId)}/${encodeURIComponent(classId)}/divergence?refresh=${refresh}`
    ),
}

// 온톨로지 관련 API
export const ontologyAPI = {
  getSchema: classRegistryAPI.getSchema,
  updateSchema: (_schema: OntologySchema, _projectId?: string) =>
    unsupportedLegacyTBoxWrite('updateSchema'),
  addClass: (_ontologyClass: OntologyClassDef, _projectId?: string) =>
    unsupportedLegacyTBoxWrite('addClass'),
  updateClass: (
    _className: string,
    _ontologyClass: OntologyClassDef,
    _projectId?: string
  ) => unsupportedLegacyTBoxWrite('updateClass'),
  deleteClass: (_className: string, _projectId?: string) =>
    unsupportedLegacyTBoxWrite('deleteClass'),
  addProperty: (_ontologyProperty: OntologyPropertyDef, _projectId?: string) =>
    unsupportedLegacyTBoxWrite('addProperty'),
  updateProperty: (
    _propertyName: string,
    _ontologyProperty: OntologyPropertyDef,
    _projectId?: string
  ) => unsupportedLegacyTBoxWrite('updateProperty'),
  deleteProperty: (_propertyName: string, _projectId?: string) =>
    unsupportedLegacyTBoxWrite('deleteProperty'),
  validateSchema: (_schema: OntologySchema, _projectId?: string) =>
    unsupportedLegacyTBoxWrite('validateSchema'),
  addSampleData: (_data: Record<string, unknown>[]) =>
    unsupportedLegacyTBoxWrite('addSampleData'),
  importCSV: (_file: File) => unsupportedLegacyTBoxWrite('importCSV'),
  exportTurtle: (_projectId?: string) => unsupportedLegacyTBoxWrite('exportTurtle'),
}

// 관계명 관리 API
export const relationsAPI = {
  getProjectRelations: (projectId: string) =>
    api.get(`/api/projects/${projectId}/relations/`),
  createRelation: (
    projectId: string,
    relation: {
      relationId: string
      label: string
      description?: string
      label_template?: string
      is_global?: boolean
    }
  ) => api.post(`/api/projects/${projectId}/relations/`, relation),
  updateRelation: (
    projectId: string,
    relationId: string,
    relation: {
      relationId: string
      label: string
      description?: string
      label_template?: string
    }
  ) => api.put(`/api/projects/${projectId}/relations/${relationId}`, relation),
  deleteRelation: (projectId: string, relationId: string) =>
    api.delete(`/api/projects/${projectId}/relations/${relationId}`),
  createBulkRelations: (
    projectId: string,
    relations: {
      relationId: string
      label: string
      description?: string
      label_template?: string
    }[]
  ) => api.post(`/api/projects/${projectId}/relations/bulk`, relations),
  getRelationEdgeProperties: (projectId: string, relationId: string) =>
    api.get(
      `/api/projects/${projectId}/relations/${relationId}/edge-properties`
    ),
  addRelationEdgeProperty: (
    projectId: string,
    relationId: string,
    data: { property_id: string; is_required?: boolean; display_order?: number }
  ) =>
    api.post(
      `/api/projects/${projectId}/relations/${relationId}/edge-properties`,
      data
    ),
  updateRelationEdgeProperty: (
    projectId: string,
    relationId: string,
    edgePropertyId: number,
    data: { is_required?: boolean; display_order?: number }
  ) =>
    api.put(
      `/api/projects/${projectId}/relations/${relationId}/edge-properties/${edgePropertyId}`,
      data
    ),
  deleteRelationEdgeProperty: (
    projectId: string,
    relationId: string,
    edgePropertyId: number
  ) =>
    api.delete(
      `/api/projects/${projectId}/relations/${relationId}/edge-properties/${edgePropertyId}`
    ),
  getRelationshipInstancesStats: (projectId: string) =>
    api.get(`/api/projects/${projectId}/relationship-instances-stats`),
}

// 온톨로지 스키마 API
export const ontologySchemaAPI = {
  getProjectSchema: (projectId: string) =>
    api.get(`/api/projects/${projectId}/ontology/schema`),
  updateProjectSchema: (projectId: string, schemaData: OntologySchema) =>
    api.put(`/api/projects/${projectId}/ontology/schema`, { schemaData }),
  deleteProjectSchema: (projectId: string) =>
    api.delete(`/api/projects/${projectId}/ontology/schema`),
}

// 관계 속성 API
export const relationshipPropertiesAPI = {
  getRelationProperties: (projectId: string, relationId: string) =>
    api
      .get(`/api/projects/${projectId}/relations/${relationId}/edge-properties`)
      .then((response) => ({
        ...response,
        data: {
          relation_id: relationId,
          properties: response.data,
          count: Array.isArray(response.data) ? response.data.length : 0,
        },
      })),
  linkProperty: (
    projectId: string,
    property: {
      relation_id: string
      property_id: string
      is_required?: boolean
      default_value?: string | number | boolean | null
    }
  ) =>
    api.post(
      `/api/projects/${projectId}/relations/${property.relation_id}/edge-properties`,
      {
        property_id: property.property_id,
        is_required: property.is_required,
      }
    ),
  deleteProperty: (projectId: string, relationId: string, propertyId: string) =>
    api
      .get(`/api/projects/${projectId}/relations/${relationId}/edge-properties`)
      .then((response) => {
        const properties = Array.isArray(response.data) ? response.data : []
        const edgeProperty = properties.find(
          (property: { property_id?: string }) =>
            property.property_id === propertyId
        )
        if (!edgeProperty?.id) {
          throw new Error(
            `Relation edge property '${propertyId}' was not found for relation '${relationId}'`
          )
        }
        return api.delete(
          `/api/projects/${projectId}/relations/${relationId}/edge-properties/${edgeProperty.id}`
        )
      }),
  createEnrichedRelationship: (
    projectId: string,
    relationship: {
      subject_id: string
      relation_id: string
      object_id: string
      properties: Record<string, unknown>
    }
  ) =>
    api.post(
      `/api/relationship-properties/create-enriched?project_id=${projectId}`,
      relationship
    ),
  getEnrichedRelationships: (
    projectId: string,
    filters?: {
      subject_id?: string
      relation_id?: string
      object_id?: string
    }
  ) => {
    const params = new URLSearchParams()
    params.append('project_id', projectId)
    if (filters?.subject_id) params.append('subject_id', filters.subject_id)
    if (filters?.relation_id) params.append('relation_id', filters.relation_id)
    if (filters?.object_id) params.append('object_id', filters.object_id)
    return api.get(`/api/relationship-properties/enriched?${params.toString()}`)
  },
}

// 데이터 속성 관리 API
export const dataPropertiesAPI = {
  getAllDataProperties: (projectId: string, includeGlobal: boolean = true) =>
    api.get(
      `/api/projects/${projectId}/data-properties/?include_global=${includeGlobal}`
    ),
  createDataProperty: (
    propertyData: {
      id: string
      label: string
      description?: string
      data_type: string
      is_global?: boolean
      scope?: string
      required?: boolean
      min_value?: number
      max_value?: number
      pattern?: string
      unit?: string
    },
    projectId: string
  ) => api.post(`/api/projects/${projectId}/data-properties/`, propertyData),
  updateDataProperty: (
    propertyId: string,
    propertyData: {
      label?: string
      description?: string
      data_type?: string
      required?: boolean
      min_value?: number
      max_value?: number
      pattern?: string
      unit?: string
    },
    projectId: string
  ) =>
    api.put(
      `/api/projects/${projectId}/data-properties/${propertyId}`,
      propertyData
    ),
  deleteDataProperty: (propertyId: string, projectId: string) =>
    api.delete(`/api/projects/${projectId}/data-properties/${propertyId}`),
}

// 클래스 타입 관리 API
export const classTypesAPI = {
  getAllClassTypes: (projectId: string) =>
    api.get(`/api/projects/${projectId}/class-types/`),
  getClassTypeProperties: (typeId: string, projectId: string) =>
    api.get(`/api/projects/${projectId}/class-types/${typeId}/properties`),
  assignPropertyToType: (
    typeId: string,
    propertyId: string,
    assignmentData: {
      is_required?: boolean
      display_order?: number
    },
    projectId: string
  ) =>
    api.post(
      `/api/projects/${projectId}/class-types/${typeId}/properties/${propertyId}`,
      assignmentData
    ),
  unassignPropertyFromType: (
    typeId: string,
    propertyId: string,
    projectId: string
  ) =>
    api.delete(
      `/api/projects/${projectId}/class-types/${typeId}/properties/${propertyId}`
    ),
  createClassType: (
    classTypeData: {
      type_id: string
      type_name: string
      description?: string
      color?: string
    },
    projectId: string
  ) => api.post(`/api/projects/${projectId}/class-types/`, classTypeData),
  updateClassType: (
    typeId: string,
    classTypeData: {
      type_name?: string
      description?: string
      color?: string
    },
    projectId: string
  ) =>
    api.put(`/api/projects/${projectId}/class-types/${typeId}`, classTypeData),
  deleteClassType: (typeId: string, projectId: string) =>
    api.delete(`/api/projects/${projectId}/class-types/${typeId}`),
  initializeClassTypes: (projectId: string) =>
    api.post(`/api/projects/${projectId}/class-types/initialize`),
  getClassTypeStats: (projectId: string) =>
    api.get(`/api/projects/${projectId}/class-types/stats`),
}

// 시스템 상태 확인 API
export const instancesAPI = {
  getClassInstances: (projectId: string, classId: string) =>
    api.get(`/api/instances/${projectId}/class/${classId}`),
  getProjectInstances: (projectId: string) =>
    api.get(`/api/instances/${projectId}/all`),
  createInstance: (projectId: string, instanceData: InstanceData) =>
    api.post(`/api/instances/${projectId}/instance`, instanceData),
  updateInstance: (
    projectId: string,
    instanceId: string,
    instanceData: Partial<InstanceData>
  ) =>
    api.put(`/api/instances/${projectId}/instance/${instanceId}`, instanceData),
  deleteInstance: (projectId: string, instanceId: string) =>
    api.delete(`/api/instances/${projectId}/instance/${instanceId}`),
  getStatistics: (projectId: string) =>
    api.get(`/api/instances/${projectId}/stats`),
}

// 클래스 키필드 API
export const classKeyFieldsAPI = {
  getAll: (projectId: string) =>
    api.get(`/api/projects/${projectId}/class-key-fields`),
  getByClassId: (projectId: string, classId: string) =>
    api.get(`/api/projects/${projectId}/class-key-fields/${classId}`),
  create: (
    projectId: string,
    data: {
      classId: string
      keyPropertyIds: string[]
      uriTemplate?: string
    }
  ) => api.post(`/api/projects/${projectId}/class-key-fields`, data),
  update: (
    projectId: string,
    classId: string,
    data: {
      keyPropertyIds?: string[]
      uriTemplate?: string
    }
  ) => api.put(`/api/projects/${projectId}/class-key-fields/${classId}`, data),
  delete: (projectId: string, classId: string) =>
    api.delete(`/api/projects/${projectId}/class-key-fields/${classId}`),
}
